import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageTitle } from "../../components/Layout.tsx";
import { fetchCategories } from "../../services/categories.service.ts";
import type { CategorySummary } from "../../types/Category.ts";
import StyledButton from "../../components/Button.tsx";
import CreateCategoryModal from "../../components/CreateCategoryModal.tsx";
import StandardCategoriesModal from "../../components/StandardCategoriesModal.tsx";
import { parseApiError } from "../../utils/errorHandler.ts";

const PageHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const Section = styled.section`
  margin-bottom: 3rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SectionHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const SectionTitleBlock = styled.div`
  min-width: 0;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: #001f52;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const SectionSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.4;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const SectionActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.15rem;
`;

const CategoryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const CategoryCard = styled.button`
  border: none;
  background: white;
  color: #001f52;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  border-top: 4px solid #64da4c;
  transition: all 0.2s ease;
  cursor: pointer;
  
  .dark-mode & {
    background: #2a2a3e;
    color: #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 31, 82, 0.15);
  }

  .dark-mode &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;

const CategoryName = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  color: #001f52;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const CountsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const CountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`;

const CountLabel = styled.span`
  font-size: 0.85rem;
  color: #666;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

const Count = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #64da4c;
`;

const SmallText = styled.p`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.75rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

const EmptySectionHint = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #888;
  font-style: italic;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

export default function CategoriesPage() {
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStandardListOpen, setIsStandardListOpen] = useState(false);
  const navigate = useNavigate();

  const standardCategories = useMemo(
    () => categories.filter((c) => c.type === "standard"),
    [categories]
  );
  const customCategories = useMemo(
    () => categories.filter((c) => c.type === "custom"),
    [categories]
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCategories("standard"), fetchCategories("custom")])
      .then(([standardRes, customRes]) =>
        setCategories([...standardRes.data, ...customRes.data])
      )
      .catch((err) => setError(parseApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const handleOpenCategory = (cat: CategorySummary) => {
    navigate(`/categories/${cat.type}/${cat.id}`);
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Kategorier</PageTitle>
      </PageHeader>

      {error && <p style={{ color: "#d32f2f" }}>{error}</p>}
      {loading && <p style={{ color: "#666" }}>Indlæser kategorier...</p>}

      {!loading && (
        <>
          <Section aria-labelledby="section-standardkategorier">
            <SectionHeaderRow>
              <SectionTitleBlock>
                <SectionTitle id="section-standardkategorier">
                  Standardkategorier
                </SectionTitle>
                <SectionSubtitle>
                  Kategorier som I abonnerer på
                </SectionSubtitle>
              </SectionTitleBlock>
              <SectionActions>
                <StyledButton
                  type="button"
                  onClick={() => setIsStandardListOpen(true)}
                >
                  Se alle standardkategorier
                </StyledButton>
              </SectionActions>
            </SectionHeaderRow>
            {standardCategories.length === 0 ? (
              <EmptySectionHint>I har ingen standardkategorier endnu.</EmptySectionHint>
            ) : (
              <CategoryList>
                {standardCategories.map((cat) => (
                  <CategoryCard
                    key={`standard-${cat.id}`}
                    type="button"
                    onClick={() => handleOpenCategory(cat)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <CategoryName>{cat.name}</CategoryName>
                    </div>
                    <SmallText>{cat.description || "Ingen beskrivelse"}</SmallText>
                    <CountsContainer>
                      <CountRow>
                        <CountLabel>PDF dokumenter:</CountLabel>
                        <Count>{cat.pdfDocumentCount}</Count>
                      </CountRow>
                      <CountRow>
                        <CountLabel>Samtaler:</CountLabel>
                        <Count>{cat.conversationCount}</Count>
                      </CountRow>
                    </CountsContainer>
                  </CategoryCard>
                ))}
              </CategoryList>
            )}
          </Section>

          <Section aria-labelledby="section-egne-kategorier">
            <SectionHeaderRow>
              <SectionTitleBlock>
                <SectionTitle id="section-egne-kategorier">Egne kategorier</SectionTitle>
                <SectionSubtitle>
                  Kategorier som I selv har oprettet
                </SectionSubtitle>
              </SectionTitleBlock>
              <SectionActions>
                <StyledButton
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                >
                  Opret ny kategori
                </StyledButton>
                <StyledButton
                  type="button"
                  onClick={() => navigate("/categories/document-status")}
                >
                  Se dokumentstatus
                </StyledButton>
              </SectionActions>
            </SectionHeaderRow>
            {customCategories.length === 0 ? (
              <EmptySectionHint>I har ikke oprettet nogen kategorier endnu.</EmptySectionHint>
            ) : (
              <CategoryList>
                {customCategories.map((cat) => (
                  <CategoryCard
                    key={`custom-${cat.id}`}
                    type="button"
                    onClick={() => handleOpenCategory(cat)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <CategoryName>{cat.name}</CategoryName>
                    </div>
                    <SmallText>{cat.description || "Ingen beskrivelse"}</SmallText>
                    <CountsContainer>
                      <CountRow>
                        <CountLabel>PDF dokumenter:</CountLabel>
                        <Count>{cat.pdfDocumentCount}</Count>
                      </CountRow>
                      <CountRow>
                        <CountLabel>Samtaler:</CountLabel>
                        <Count>{cat.conversationCount}</Count>
                      </CountRow>
                    </CountsContainer>
                  </CategoryCard>
                ))}
              </CategoryList>
            )}
          </Section>
        </>
      )}

      <CreateCategoryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <StandardCategoriesModal
        isOpen={isStandardListOpen}
        onClose={() => setIsStandardListOpen(false)}
      />
    </PageContainer>
  );
}
